// src/pages/MenuItemsPage.js - Full CRUD for food items (no image upload)
import React, { useState, useEffect } from "react";
import { menuAPI } from "../api/api";
import {
  Modal,
  FormRow,
  ModalActions,
  ConfirmModal,
  NumInput,
} from "./ClassesPage";
import toast from "react-hot-toast";
import "./CommonPage.css";

const UNITS = ["grams", "ml", "count", "rupees", "paise"];
const UNIT_KN = {
  grams: "ಗ್ರಾಂ",
  ml: "ಮಿಲಿ",
  count: "ಸಂಖ್ಯೆ",
  rupees: "ರೂಪಾಯಿ",
  paise: "ಪೈಸೆ",
};
const CATS = [
  "grain",
  "dal",
  "vegetable",
  "egg",
  "oil",
  "spice",
  "fruit",
  "other",
];
const CAT_KN = {
  grain: "ಧಾನ್ಯ",
  dal: "ಬೇಳೆ",
  vegetable: "ತರಕಾರಿ",
  egg: "ಮೊಟ್ಟೆ",
  oil: "ಎಣ್ಣೆ",
  spice: "ಮಸಾಲೆ",
  fruit: "ಹಣ್ಣು",
  other: "ಇತರ",
};
const CAT_ICON = {
  grain: "🌾",
  dal: "🫘",
  vegetable: "🥦",
  egg: "🥚",
  oil: "🫙",
  spice: "🧂",
  fruit: "🍌",
  other: "🍽️",
};
const SUP_KN = {
  pds: "ಸರ್ಕಾರಿ ಪಡಿತರ",
  local_purchase: "ಸ್ಥಳೀಯ ಖರೀದಿ",
  both: "ಎರಡೂ",
};
const BLANK = {
  name_kannada: "",
  name_english: "",
  unit_type: "grams",
  unit_label_kannada: "ಗ್ರಾಂ",
  unit_label_english: "grams",
  category: "grain",
  supply_type: "local_purchase",
  display_order: "1",
  is_active: 1,
  is_supplementary: 0, 
};

export default function MenuItemsPage() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null); // 'form'|'delete'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");

  const load = () => {
    menuAPI
      .list({ active_only: false })
      .then((r) => setItems(r.data.data || []))
      .catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };

  const openAdd = () => {
    setForm(BLANK);
    setSelected(null);
    setModal("form");
  };
  const openEdit = (item) => {
    setSelected(item);
    setForm({
      name_kannada: item.name_kannada,
      name_english: item.name_english,
      unit_type: item.unit_type,
      unit_label_kannada: item.unit_label_kannada || "",
      unit_label_english: item.unit_label_english || "",
      category: item.category,
      supply_type: item.supply_type,
      display_order: String(item.display_order || 1),
      is_active: item.is_active,
       is_supplementary: item.is_supplementary || 0,  
    });
    setModal("form");
  };

  const setF = (k, v) =>
    setForm((p) => {
      const n = { ...p, [k]: v };
      if (k === "unit_type") {
        n.unit_label_kannada = UNIT_KN[v] || "";
        n.unit_label_english = v;
      }
      return n;
    });

  const handleSave = async () => {
  // 1. Kannada compulsory
  if (!form.name_kannada.trim()) {
    toast.error("ಕನ್ನಡ ಹೆಸರು ಅಗತ್ಯ ❗");
    return;
  }

  
      // 2. English letters not allowed
  const hasEnglishLetters = /[A-Za-z]/.test(form.name_kannada);

  if (hasEnglishLetters) {
    toast.error("English letters ಬೇಡ ❗");
    return;
  }
  
  // 3. Duplicate check
  const normalize = (s) => s.trim().toLowerCase();
  const alreadyExists = items.some(
    (i) =>
      normalize(i.name_kannada) === normalize(form.name_kannada) &&
      (!selected || i.id !== selected.id)
  );

  if (alreadyExists) {
    toast.error("ಈ ಹೆಸರು ಈಗಾಗಲೇ ಇದೆ ❌");
    return;
  }

  setLoading(true);
  try {
    if (selected) {
      await menuAPI.update(selected.id, form);
      toast.success("ಆಹಾರ ಪದಾರ್ಥ ಅಪ್‌ಡೇಟ್ ಆಗಿದೆ ✅");
    } else {
      await menuAPI.create(form);
      toast.success("ಆಹಾರ ಪದಾರ್ಥ ಮಾಡಲಾಗಿದೆ ✅");
    }
    closeModal();
    load();
  } catch (e) {
    toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
  } finally {
    setLoading(false);
  }
};

  const handleDelete = async () => {
    setLoading(true);
    try {
      await menuAPI.update(selected.id, { ...form, is_active: 0 });
      toast.success("ಆಹಾರ ಪದಾರ್ಥ ನಿಷ್ಕ್ರಿಯ ಮಾಡಲಾಗಿದೆ");
      closeModal();
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "ತಪ್ಪಾಗಿದೆ");
    } finally {
      setLoading(false);
    }
  };

  // Filter
  const filtered = items.filter((i) => {
    const matchCat = catFilter === "all" || i.category === catFilter;
    const matchSearch =
      !search ||
      i.name_kannada.includes(search) ||
      i.name_english.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });
  const activeItems = filtered.filter((i) => i.is_active);
  const inactiveItems = filtered.filter((i) => !i.is_active);

  return (
    <div className="common-page">
      <div className="page-header">
        <h1 className="page-title">🥘 ಆಹಾರ ಪದಾರ್ಥಗಳ ನಿರ್ವಹಣೆ</h1>
        <p className="page-desc">
          MDM ಆಹಾರ ಪದಾರ್ಥಗಳು, ಮಾಪನ — ಸೇರಿಸಿ, ಸಂಪಾದಿಸಿ, ಅಳಿಸಿ
        </p>
      </div>

      {/* Search & Filter */}
      <div className="list-toolbar">
        <input
          className="toolbar-search"
          placeholder="🔍 ಹೆಸರು ಹುಡುಕಿ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="toolbar-filter"
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
        >
          <option value="all">ಎಲ್ಲಾ ವರ್ಗ</option>
          {CATS.map((c) => (
            <option key={c} value={c}>
              {CAT_ICON[c]} {CAT_KN[c]}
            </option>
          ))}
        </select>
        <button className="act-btn act-green" onClick={openAdd}>
          + ಹೊಸ ಪದಾರ್ಥ
        </button>
      </div>

      {/* Items grid */}
      <div className="items-grid">
        {activeItems.map((item) => (
          <div key={item.id} className="item-card">
            <div className="item-img-placeholder">
              {CAT_ICON[item.category] || "🍽️"}
            </div>
            <div className="item-body">
              <div className="item-name-kn">{item.name_kannada}</div>
              <div className="item-name-en">{item.name_english}</div>
              <div className="item-tags">
                <span className="itag itag-unit">
                  {item.unit_label_kannada || item.unit_type}
                </span>
                <span className="itag itag-cat">{CAT_KN[item.category]}</span>
                <span className="itag itag-supply">
                  {SUP_KN[item.supply_type]}
                </span>
              </div>
              <div className="item-actions">
                <button
                  className="item-act-btn item-edit"
                  onClick={() => openEdit(item)}
                >
                  ✏️ ಸಂಪಾದಿಸಿ
                </button>
                <button
                  className="item-act-btn item-del"
                  style={{ flex: "none", padding: "0.4rem 0.6rem" }}
                  onClick={() => {
                    setSelected(item);
                    openEdit(item);
                    setTimeout(() => setModal("delete"), 10);
                  }}
                  title="ಅಳಿಸಿ"
                >
                  🗑️
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {activeItems.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "2rem",
            color: "rgba(255,255,255,0.3)",
            fontFamily: "Noto Sans Kannada",
          }}
        >
          ಯಾವ ಪದಾರ್ಥ ಕಂಡುಬಂದಿಲ್ಲ
        </div>
      )}

      {inactiveItems.length > 0 && (
        <details style={{ marginTop: "1rem" }}>
          <summary
            style={{
              fontFamily: "Noto Sans Kannada",
              fontSize: "0.78rem",
              color: "rgba(255,255,255,0.4)",
              cursor: "pointer",
              padding: "0.4rem 0",
            }}
          >
            ನಿಷ್ಕ್ರಿಯ ಪದಾರ್ಥಗಳು ({inactiveItems.length})
          </summary>
          <div
            className="items-grid"
            style={{ marginTop: "0.6rem", opacity: 0.5 }}
          >
            {inactiveItems.map((item) => (
              <div key={item.id} className="item-card">
                <div className="item-img-placeholder">
                  {CAT_ICON[item.category] || "🍽️"}
                </div>
                <div className="item-body">
                  <div className="item-name-kn">{item.name_kannada}</div>
                  <div className="item-actions">
                    <button
                      className="item-act-btn item-edit"
                      onClick={() => openEdit(item)}
                    >
                      ♻️ ಮರು-ಸಕ್ರಿಯ
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Add/Edit Modal */}
      {modal === "form" && (
        <Modal
          title={selected ? "ಆಹಾರ ಪದಾರ್ಥ ಸಂಪಾದಿಸಿ" : "ಹೊಸ ಆಹಾರ ಪದಾರ್ಥ"}
          onClose={closeModal}
          wide
        >
          <FormRow label="ಕನ್ನಡ ಹೆಸರು *">
            <input
              className="modal-input"
              placeholder="ಅಕ್ಕಿ"
              value={form.name_kannada}
              onChange={(e) => setF("name_kannada", e.target.value)}
            />
          </FormRow>
          <FormRow label="English ಹೆಸರು" hint="(ಐಚ್ಛಿಕ)">
            <input
              className="modal-input"
              placeholder="Rice (optional)"
              value={form.name_english}
              onChange={(e) => setF("name_english", e.target.value)}
            />
          </FormRow>
          <FormRow label="ಮಾಪನ ಪ್ರಕಾರ *">
            <select
              className="modal-input"
              value={form.unit_type}
              onChange={(e) => setF("unit_type", e.target.value)}
            >
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {UNIT_KN[u]} ({u})
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="ವರ್ಗ">
            <select
              className="modal-input"
              value={form.category}
              onChange={(e) => setF("category", e.target.value)}
            >
              {CATS.map((c) => (
                <option key={c} value={c}>
                  {CAT_ICON[c]} {CAT_KN[c]}
                </option>
              ))}
            </select>
          </FormRow>
          <FormRow label="ಪೂರೈಕೆ ಮೂಲ">
            <select
              className="modal-input"
              value={form.supply_type}
              onChange={(e) => setF("supply_type", e.target.value)}
            >
              <option value="pds">ಸರ್ಕಾರಿ ಪಡಿತರ (PDS)</option>
              <option value="local_purchase">ಸ್ಥಳೀಯ ಖರೀದಿ</option>
              <option value="both">ಎರಡೂ</option>
            </select>
          </FormRow>
          <FormRow label="ಪೂರಕ ಪೌಷ್ಟಿಕ ಆಹಾರ">
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      cursor: "pointer",
      fontFamily: "Noto Sans Kannada",
    }}
  >
    <input
      type="checkbox"
      checked={!!form.is_supplementary}
      onChange={(e) =>
        setF("is_supplementary", e.target.checked ? 1 : 0)
      }
    />
   (ಮೊಟ್ಟೆ, ಬಾಳೆಹಣ್ಣು, ಚಿಕ್ಕಿ, ಹಾಲು...)
  </label>
</FormRow>
          <FormRow
            label="ಪ್ರದರ್ಶನ ಕ್ರಮ"
            hint="(ಐಚ್ಛಿಕ — ಖಾಲಿ ಬಿಟ್ಟರೆ ತಾನೇ ನಿಗದಿ)"
          >
            <NumInput
              value={form.display_order}
              onChange={(v) => setF("display_order", v)}
              placeholder="ಖಾಲಿ = ತಾನೇ"
            />
          </FormRow>
          {selected && (
            <FormRow label="ಸ್ಥಿತಿ">
              <select
                className="modal-input"
                value={form.is_active}
                onChange={(e) => setF("is_active", parseInt(e.target.value))}
              >
                <option value={1}>✅ ಸಕ್ರಿಯ</option>
                <option value={0}>❌ ನಿಷ್ಕ್ರಿಯ</option>
              </select>
            </FormRow>
          )}
          <ModalActions
            loading={loading}
            onSave={handleSave}
            onCancel={closeModal}
            saveLabel={selected ? "ಅಪ್‌ಡೇಟ್ ಮಾಡಿ" : "ಮಾಡಿ"}
          />
        </Modal>
      )}

      {modal === "delete" && (
        <ConfirmModal
          title="ಆಹಾರ ಪದಾರ್ಥ ನಿಷ್ಕ್ರಿಯ ಮಾಡಿ"
          message={`"${selected?.name_kannada}" ಅನ್ನು ನಿಷ್ಕ್ರಿಯ ಮಾಡುವಿರಾ?`}
          onConfirm={handleDelete}
          onCancel={closeModal}
          loading={loading}
        />
      )}

      <style>{`
        .list-toolbar{display:flex;flex-wrap:wrap;gap:.7rem;align-items:center;margin-bottom:1.2rem}
        .toolbar-search{background:rgba(255,255,255,.06);border:1.5px solid rgba(255,255,255,.12);border-radius:10px;color:#fff;padding:.6rem 1rem;font-family:'Noto Sans Kannada',sans-serif;font-size:.85rem;outline:none;flex:1;min-width:160px}
        .toolbar-search::placeholder{color:rgba(255,255,255,.3)}
        .toolbar-filter{background:rgba(255,255,255,.06);border:1.5px solid rgba(255,255,255,.12);border-radius:10px;color:#fff;padding:.6rem .8rem;font-family:'Noto Sans Kannada',sans-serif;font-size:.82rem;outline:none}
        .toolbar-filter option{background:#0a1628}
      `}</style>
    </div>
  );
}
